import { Stack } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useDict } from "@/utils/useDict";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  buildConditionInsightCharts,
  buildConditionPatientRows,
} from "src-next/modules/clinical-insights/transformers/mapConditionInsights";
import { matchesPractitionerFilter } from "src-next/modules/clinical-insights/helpers/filterResourcesByPractitioner";
import { fetchInsightConditions } from "src-next/modules/clinical-insights/services/fetchInsightOrganizationBundle";
import { useFhirInsightView } from "src-next/modules/clinical-insights/hooks/useFhirInsightView";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";

const texts = getInsightAnalysisTexts("diagnosticos");

function conditionPatientIds(conditions: fhir5.Condition[]): string[] {
  return conditions.map((condition) => condition.subject?.reference || "");
}

export function DiagnosticAnalysis({
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
  fetchWarm,
}: ClinicalInsightChildProps) {
  const { organizationId } = useOrganizationContext();
  const practitionerFilter = practitionerId || "all";
  const dict = useDict;
  const warm = fetchWarm ?? isActive;

  const verificationLabel = useCallback(
    (code: string) => dict.verificationStatus[code] || code,
    [dict.verificationStatus]
  );

  const view = useFhirInsightView({
    analysisId: "diagnosticos",
    organizationId: organizationId || "",
    dateRange,
    enabled: warm,
    isActive,
    onLoadPhaseChange,
    selectPatientIds: conditionPatientIds,
    fetcher: () =>
      fetchInsightConditions({
        organizationId: organizationId || "",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      }),
  });

  const conditions = useMemo(() => {
    if (practitionerFilter === "all") return view.resources;
    return view.resources.filter((cond) =>
      matchesPractitionerFilter(practitionerFilter, cond.recorder?.reference)
    );
  }, [view.resources, practitionerFilter]);

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;
  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel
  );

  const { statusItems, rankingItems, total } = useMemo(
    () => buildConditionInsightCharts(conditions, verificationLabel),
    [conditions, verificationLabel]
  );

  const patientRows = useMemo(
    () => buildConditionPatientRows(conditions, view.patients, verificationLabel),
    [conditions, view.patients, verificationLabel]
  );

  const statusLabelByKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of statusItems) {
      map[item.key] = item.label;
    }
    return map;
  }, [statusItems]);

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const percentage = total > 0 ? Math.round((sorted[0].value / total) * 100) : 0;
    return `${percentage}% dos ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, total]);

  const busy = view.isLoading || view.patientsLoading;

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#2196f3"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={view.loadPhase}
        patientsProgress={view.patientsProgress}
        hasData={view.hasPayload || conditions.length > 0}
        isEmpty={total === 0 && !busy}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={() => {
          void view.refresh();
        }}
        statusChart={{ insightText: statusInsightTitle, items: statusItems }}
        rankingChart={{ insightText: texts.rankingHint, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={statusLabelByKey}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
